import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  TextField,
  MenuItem,
  Avatar,
  CircularProgress,
  InputAdornment,
} from "@mui/material";

import {
  FaSearch,
  FaCheck,
  FaTimes,
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaFileExcel,
} from "react-icons/fa";

import Swal from "sweetalert2";
import * as XLSX from "xlsx";

import api from "../../api";
import "./StudentAttendanceList.css";

/* =========================================================
   API
   ========================================================= */

const API_BASE = "/teacher-attendance";

/* =========================================================
   TODAY
   ========================================================= */

const getToday = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/* =========================================================
   DATE FORMAT
   ========================================================= */

const formatDisplayDate = (dateString) => {
  if (!dateString) {
    return "";
  }

  const date = new Date(`${dateString}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* =========================================================
   NORMALIZE EMPLOYEE TYPE
   ========================================================= */

const normalizeEmployeeType = (value) => {
  if (!value) {
    return "";
  }

  const v = String(value).trim().toUpperCase().replace(/[- ]/g, "_");

  if (v === "TEACHER" || v === "TEACHING") {
    return "TEACHING";
  }

  if (
    v === "NON_TEACHER" ||
    v === "NON_TEACHING" ||
    v === "NONTEACHING"
  ) {
    return "NON_TEACHING";
  }

  return "";
};

/* =========================================================
   NORMALIZE STATUS
   ========================================================= */

const normalizeStatus = (value) => {
  if (value === null || value === undefined || value === "") {
    return "not_marked";
  }

  const status = String(value).trim().toLowerCase();

  if (status === "p" || status === "present") {
    return "present";
  }

  if (status === "a" || status === "absent") {
    return "absent";
  }

  if (status === "l" || status === "leave") {
    return "leave";
  }

  return "not_marked";
};

/* =========================================================
   GET SCHOOL ID
   ========================================================= */

const getSchoolId = () => {
  return localStorage.getItem("school_id") || "";
};

/* =========================================================
   EXTRACT ARRAY
   ========================================================= */

const extractArray = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.staff)) {
    return data.staff;
  }

  return [];
};

/* =========================================================
   TEACHER ATTENDANCE PAGE
   ========================================================= */

const TeacherAttendancePage = () => {
  const schoolId = getSchoolId();

  const [attendanceDate, setAttendanceDate] = useState(getToday());
  const [employeeType, setEmployeeType] = useState("");
  const [types, setTypes] = useState([
    { id: "TEACHING", value: "TEACHING", name: "Teaching" },
    { id: "NON_TEACHING", value: "NON_TEACHING", name: "Non Teaching" },
  ]);
  const [staff, setStaff] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [staffLoaded, setStaffLoaded] = useState(false);

  /* =========================================================
     LOAD TYPES
     ========================================================= */
  const loadEmployeeTypes = async () => {
    try {
      setLoadingTypes(true);

      const response = await api.get(`${API_BASE}/types`, {
        headers: { "X-School-Id": schoolId },
      });

      const list = extractArray(response);

      if (list.length) {
        setTypes(list);
      }
    } catch (err) {
      console.error("Employee type error:", err);
    } finally {
      setLoadingTypes(false);
    }
  };

  useEffect(() => {
    loadEmployeeTypes();
  }, []);

  /* =========================================================
     LOAD STAFF
     ========================================================= */
  const loadStaff = async () => {
    if (!schoolId) {
      Swal.fire({
        icon: "error",
        title: "School ID Missing",
        text: "School ID is not available. Please login again.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    if (!attendanceDate) {
      Swal.fire({
        icon: "warning",
        title: "Required",
        text: "Please select attendance date.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    if (!employeeType) {
      Swal.fire({
        icon: "warning",
        title: "Required",
        text: "Please select Teaching or Non Teaching.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    const normalizedType = normalizeEmployeeType(employeeType);

    if (!normalizedType) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Employee Type",
        text: "Please select a valid employee type.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    setLoadingStaff(true);
    setStaffLoaded(false);
    setSearchQuery("");
    setStaff([]);

    try {
      const response = await api.get(`${API_BASE}/staff`, {
        params: {
          date: attendanceDate,
          employee_type: normalizedType,
          school_id: schoolId,
        },
        headers: { "X-School-Id": schoolId },
      });

      const list = extractArray(response);

      const normalized = list.map((item, index) => {
        const id =
          item?.teacher_id ??
          item?.staff_id ??
          item?.employee_id ??
          item?.id;

        const savedStatus = item?.attendance_status ?? null;

        return {
          ...item,
          id,
          teacher_id: id,
          attendance_status: normalizeStatus(savedStatus),
          _index: index,
        };
      });

      setStaff(normalized);
      setStaffLoaded(true);

      if (normalized.length === 0) {
        Swal.fire({
          icon: "info",
          title: "No Staff Found",
          text: `No ${
            normalizedType === "TEACHING" ? "teaching" : "non teaching"
          } staff found for the selected school.`,
          confirmButtonText: "OK",
          confirmButtonColor: "#2563eb",
        });
      }
    } catch (err) {
      console.error("Teacher attendance load error:", err);

      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Unable to load staff. Please try again.";

      setStaff([]);
      setStaffLoaded(true);

      Swal.fire({
        icon: "error",
        title: "Unable to Load Staff",
        text: message,
        confirmButtonText: "OK",
      });
    } finally {
      setLoadingStaff(false);
    }
  };

  /* =========================================================
     CHANGE STATUS
     ========================================================= */
  const changeStatus = (teacherId, status) => {
    setStaff((previous) =>
      previous.map((member) => {
        if (String(member.id) === String(teacherId)) {
          return { ...member, attendance_status: status };
        }
        return member;
      })
    );
  };

  const selectAllPresent = () => {
    setStaff((previous) =>
      previous.map((member) => ({
        ...member,
        attendance_status: "present",
      }))
    );
  };

  const markAllAbsent = () => {
    setStaff((previous) =>
      previous.map((member) => ({
        ...member,
        attendance_status: "absent",
      }))
    );
  };

  const resetAttendance = () => {
    setStaff((previous) =>
      previous.map((member) => ({
        ...member,
        attendance_status: "present",
      }))
    );
  };

  /* =========================================================
     SEARCH
     ========================================================= */
  const filteredStaff = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return staff;
    }

    return staff.filter((member) => {
      const name = String(
        member?.name ||
          member?.teacher_name ||
          member?.staff_name ||
          ""
      ).toLowerCase();

      const code = String(
        member?.employee_code ||
          member?.staff_code ||
          member?.teacher_code ||
          member?.code ||
          ""
      ).toLowerCase();

      const mobile = String(
        member?.mobile || member?.mobile_no || member?.phone || ""
      ).toLowerCase();

      const designation = String(
        member?.designation || member?.designation_name || ""
      ).toLowerCase();

      return (
        name.includes(query) ||
        code.includes(query) ||
        mobile.includes(query) ||
        designation.includes(query)
      );
    });
  }, [staff, searchQuery]);

  /* =========================================================
     SUMMARY
     ========================================================= */
  const attendanceSummary = useMemo(() => {
    const total = staff.length;

    const present = staff.filter(
      (member) => member.attendance_status === "present"
    ).length;

    const absent = staff.filter(
      (member) => member.attendance_status === "absent"
    ).length;

    const leave = staff.filter(
      (member) => member.attendance_status === "leave"
    ).length;

    return { total, present, absent, leave };
  }, [staff]);

  /* =========================================================
     SAVE
     ========================================================= */
  const saveAttendance = async () => {
    if (!schoolId) {
      Swal.fire({
        icon: "error",
        title: "School ID Missing",
        text: "School ID is not available. Please login again.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    if (!attendanceDate) {
      Swal.fire({
        icon: "warning",
        title: "Required",
        text: "Please select attendance date.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    const normalizedType = normalizeEmployeeType(employeeType);

    if (!normalizedType) {
      Swal.fire({
        icon: "warning",
        title: "Required",
        text: "Please select Teaching or Non Teaching.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    if (!staff.length) {
      Swal.fire({
        icon: "info",
        title: "No Staff Found",
        text: "There are no staff members to save.",
        confirmButtonText: "OK",
      });
      return;
    }

    const notMarked = staff.filter(
      (member) =>
        !member.attendance_status ||
        member.attendance_status === "not_marked"
    );

    if (notMarked.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Attendance Incomplete",
        text: `${notMarked.length} staff member${
          notMarked.length > 1 ? "s are" : " is"
        } not marked. Please select Present, Absent or Leave for everyone.`,
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    setSaving(true);

    try {
      const payload = {
        date: attendanceDate,
        employee_type: normalizedType,
        staff: staff.map((member) => ({
          teacher_id: Number(member.id),
          status: member.attendance_status,
        })),
      };

      const response = await api.post(`${API_BASE}/save`, payload, {
        headers: {
          "X-School-Id": schoolId,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (response?.data?.status === false) {
        throw new Error(
          response?.data?.message || "Attendance could not be saved."
        );
      }

      await loadStaff();

      Swal.fire({
        icon: "success",
        title: "Attendance Saved",
        text:
          response?.data?.message ||
          "Staff attendance has been saved successfully.",
        confirmButtonText: "OK",
        confirmButtonColor: "#16a34a",
      });
    } catch (err) {
      console.error("Teacher attendance save error:", err);

      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Attendance could not be saved.";

      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: message,
        confirmButtonText: "OK",
      });
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     STAFF HELPERS
     ========================================================= */
  const getName = (member) => {
    return (
      member?.name ||
      member?.teacher_name ||
      member?.staff_name ||
      member?.employee_name ||
      "-"
    );
  };

  const getCode = (member) => {
    return (
      member?.employee_code ||
      member?.staff_code ||
      member?.teacher_code ||
      member?.code ||
      "-"
    );
  };

  const getDesignation = (member) => {
    return (
      member?.designation_name ||
      member?.designation ||
      member?.role_name ||
      "-"
    );
  };

  const getMobile = (member) => {
    return member?.mobile || member?.mobile_no || member?.phone || "-";
  };

  const getImage = (member) => {
    const image =
      member?.photo ||
      member?.image ||
      member?.profile_image ||
      member?.avatar ||
      member?.photo_url ||
      "";

    if (!image) {
      return "/default-user.png";
    }

    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }

    return `https://dgps.co.in/${image.replace(/^\/+/, "")}`;
  };

  const getEmployeeTypeLabel = () => {
    const normalized = normalizeEmployeeType(employeeType);
    if (normalized === "TEACHING") return "Teaching";
    if (normalized === "NON_TEACHING") return "Non Teaching";
    return "";
  };

  /* =========================================================
     EXPORT TO EXCEL
     ========================================================= */
  const exportToExcel = () => {
    if (!staff || staff.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Data",
        text: "Please load staff list first to download Excel sheet.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    const typeLabel = getEmployeeTypeLabel() || "Staff";

    const excelData = staff.map((member, index) => ({
      "SL No": index + 1,
      "Staff ID": member.id || "-",
      "Staff Name": getName(member),
      "Employee Code": getCode(member),
      Designation: getDesignation(member),
      Mobile: getMobile(member),
      "Employee Type": typeLabel,
      Date: attendanceDate,
      "Attendance Status": (
        member.attendance_status || "not_marked"
      ).toUpperCase(),
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Staff Attendance");

    const fileName = `Staff_Attendance_${typeLabel}_${attendanceDate}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  /* =========================================================
     RENDER
     ========================================================= */
  return (
    <Box className="student-attendance-page">
      {/* PAGE HEADER */}
      <Box
        className="attendance-page-header"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography className="attendance-page-title">
            Teacher Attendance
          </Typography>

          <Typography className="attendance-page-subtitle">
            Manage daily staff attendance by employee type.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            color="success"
            startIcon={<FaFileExcel />}
            onClick={exportToExcel}
            disabled={!staffLoaded || staff.length === 0}
            sx={{
              backgroundColor: "#16a34a",
              "&:hover": { backgroundColor: "#15803d" },
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Export Excel
          </Button>

          <Box className="attendance-date-display">
            <FaCalendarAlt />
            <Box>
              <span>Attendance Date</span>
              <strong>{formatDisplayDate(attendanceDate)}</strong>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* FILTER CARD */}
      <Paper className="attendance-filter-card" elevation={0}>
        <Box className="attendance-filter-heading">
          <Typography className="filter-title">Attendance Details</Typography>

          <Typography className="filter-description">
            Select date and employee type to load staff.
          </Typography>
        </Box>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Attendance Date"
              type="date"
              value={attendanceDate}
              onChange={(e) => {
                setAttendanceDate(e.target.value);
                setStaff([]);
                setStaffLoaded(false);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Employee Type"
              value={employeeType}
              onChange={(e) => {
                setEmployeeType(e.target.value);
                setStaff([]);
                setStaffLoaded(false);
              }}
              disabled={loadingTypes}
            >
              <MenuItem value="">
                <em>Select Employee Type</em>
              </MenuItem>
              {types.map((type, index) => {
                const value = type?.value || type?.id || "";
                const label =
                  type?.name ||
                  (normalizeEmployeeType(value) === "NON_TEACHING"
                    ? "Non Teaching"
                    : "Teaching");

                return (
                  <MenuItem key={`${value}-${index}`} value={value}>
                    {label}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="contained"
              className="load-students-button"
              onClick={loadStaff}
              disabled={loadingStaff || !attendanceDate || !employeeType}
              startIcon={
                loadingStaff ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <FaSearch />
                )
              }
            >
              {loadingStaff ? "Loading..." : "Load Staff"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* SUMMARY */}
      <Box className="attendance-summary-grid">
        <Paper className="attendance-summary-card total-card" elevation={0}>
          <Box className="summary-icon total-icon">
            <FaChalkboardTeacher />
          </Box>
          <Box>
            <span>Total Staff</span>
            <strong>{attendanceSummary.total}</strong>
          </Box>
        </Paper>

        <Paper className="attendance-summary-card present-card" elevation={0}>
          <Box className="summary-icon present-icon">
            <FaCheck />
          </Box>
          <Box>
            <span>Present</span>
            <strong>{attendanceSummary.present}</strong>
          </Box>
        </Paper>

        <Paper className="attendance-summary-card absent-card" elevation={0}>
          <Box className="summary-icon absent-icon">
            <FaTimes />
          </Box>
          <Box>
            <span>Absent</span>
            <strong>{attendanceSummary.absent}</strong>
          </Box>
        </Paper>

        <Paper className="attendance-summary-card leave-card" elevation={0}>
          <Box className="summary-icon leave-icon">L</Box>
          <Box>
            <span>Leave</span>
            <strong>{attendanceSummary.leave}</strong>
          </Box>
        </Paper>
      </Box>

      {/* STAFF LIST */}
      {staffLoaded && (
        <Paper className="attendance-list-card" elevation={0}>
          <Box className="student-list-header">
            <Box>
              <Typography className="student-list-title">Staff List</Typography>

              <Typography className="student-list-subtitle">
                {getEmployeeTypeLabel() || "Select employee type"}
                {" • "}
                {formatDisplayDate(attendanceDate)}
              </Typography>
            </Box>

            <Box className="student-count-chip">
              {staff.length} Staff
            </Box>
          </Box>

          <Box className="attendance-toolbar">
            <TextField
              className="attendance-search"
              size="small"
              placeholder="Search staff name, code or mobile..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch />
                  </InputAdornment>
                ),
              }}
            />

            <Box className="attendance-toolbar-actions">
              <Button
                variant="outlined"
                className="toolbar-button present-toolbar-button"
                startIcon={<FaCheck />}
                onClick={selectAllPresent}
                disabled={!staff.length}
              >
                Select All Present
              </Button>

              <Button
                variant="outlined"
                className="toolbar-button absent-toolbar-button"
                startIcon={<FaTimes />}
                onClick={markAllAbsent}
                disabled={!staff.length}
              >
                Mark All Absent
              </Button>

              <Button
                variant="text"
                className="reset-button"
                onClick={resetAttendance}
                disabled={!staff.length}
              >
                Reset
              </Button>
            </Box>
          </Box>

          <Box className="attendance-table-wrapper">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>STAFF</th>
                  <th>EMPLOYEE CODE</th>
                  <th>DESIGNATION</th>
                  <th>MOBILE</th>
                  <th>ATTENDANCE</th>
                </tr>
              </thead>

              <tbody>
                {loadingStaff ? (
                  <tr>
                    <td colSpan="6" className="loading-student-cell">
                      <CircularProgress size={28} />
                      <span>Loading staff...</span>
                    </td>
                  </tr>
                ) : filteredStaff.length > 0 ? (
                  filteredStaff.map((member, index) => {
                    const status =
                      member.attendance_status || "not_marked";

                    return (
                      <tr key={member.id || index}>
                        <td>
                          <span className="row-number">{index + 1}</span>
                        </td>

                        <td>
                          <Box className="student-cell">
                            <Avatar
                              src={getImage(member)}
                              className="student-avatar"
                              imgProps={{
                                onError: (e) => {
                                  e.currentTarget.src = "/default-user.png";
                                },
                              }}
                            />

                            <Box className="student-info">
                              <strong>{getName(member)}</strong>
                              <small>ID: {member.id || "-"}</small>
                            </Box>
                          </Box>
                        </td>

                        <td>
                          <span className="table-text">{getCode(member)}</span>
                        </td>

                        <td>
                          <span className="table-text">
                            {getDesignation(member)}
                          </span>
                        </td>

                        <td>
                          <span className="table-text">{getMobile(member)}</span>
                        </td>

                        <td>
                          <Box className="status-button-group">
                            <button
                              type="button"
                              className={`attendance-status-button present ${
                                status === "present" ? "active" : ""
                              }`}
                              onClick={() =>
                                changeStatus(member.id, "present")
                              }
                            >
                              <FaCheck />
                              <span>Present</span>
                            </button>

                            <button
                              type="button"
                              className={`attendance-status-button absent ${
                                status === "absent" ? "active" : ""
                              }`}
                              onClick={() => changeStatus(member.id, "absent")}
                            >
                              <FaTimes />
                              <span>Absent</span>
                            </button>

                            <button
                              type="button"
                              className={`attendance-status-button leave ${
                                status === "leave" ? "active" : ""
                              }`}
                              onClick={() => changeStatus(member.id, "leave")}
                            >
                              <span className="leave-letter">L</span>
                              <span>Leave</span>
                            </button>
                          </Box>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="no-student-cell">
                      <Box className="no-student-content">
                        <Box className="no-student-icon">
                          <FaChalkboardTeacher />
                        </Box>
                        <strong>No Staff Found</strong>
                        <span>
                          {searchQuery.trim()
                            ? "No staff matches your search."
                            : "No staff members are available for the selected employee type."}
                        </span>
                      </Box>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>

          <Box className="attendance-footer">
            <Box className="attendance-footer-summary">
              <span>
                Total: <strong>{attendanceSummary.total}</strong>
              </span>
              <span className="footer-present">
                Present: <strong>{attendanceSummary.present}</strong>
              </span>
              <span className="footer-absent">
                Absent: <strong>{attendanceSummary.absent}</strong>
              </span>
              <span className="footer-leave">
                Leave: <strong>{attendanceSummary.leave}</strong>
              </span>
            </Box>

            <Button
              variant="contained"
              className="save-attendance-button"
              onClick={saveAttendance}
              disabled={saving || staff.length === 0}
              startIcon={
                saving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <FaCheck />
                )
              }
            >
              {saving ? "Saving..." : "Save Attendance"}
            </Button>
          </Box>
        </Paper>
      )}

      {/* BEFORE LOAD */}
      {!staffLoaded && !loadingStaff && (
        <Paper className="attendance-empty-state" elevation={0}>
          <Box className="empty-state-icon">
            <FaChalkboardTeacher />
          </Box>
          <Typography className="empty-state-title">
            Ready to Take Attendance
          </Typography>
          <Typography className="empty-state-text">
            Select the attendance date and employee type. Then click{" "}
            <strong>Load Staff</strong> to load staff and their saved
            attendance.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default TeacherAttendancePage;
